import { useState, useEffect } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  getItem,
  getProcurementResults,
  getProcurementState,
  getItemHistory,
  patchItem,
} from '../../shared/api/requestItems.api'
import type { RequestItemProcurementState } from '@crm/shared'
import {
  convertQuoteToMatrixField,
  PRICE_FIELD_BY_MATRIX,
} from '../requests/utils/convertQuoteToMatrixField'
import { useMatrixRates } from '../requests/hooks/useMatrixRates'
import { PricingNotReady } from '../requests/components/PricingNotReady'
import { PageLoader } from '../../shared/ui/Spinner'
import { ItemStatusBadge, PriorityBadge, ProcItemStatusBadge } from '../../shared/ui/Badge'
import { useAuthStore } from '../auth/authStore'
import { canOpenQueue } from '../procurement/queueAccess'
import { SiblingDropdown } from './components/SiblingDropdown'
import { ProcStateButtons } from './components/ProcStateButtons'
import { QuotesTable } from './components/QuotesTable'
import { ItemHistoryPanel } from './components/ItemHistoryPanel'
import { ItemCommentsPanel } from './components/ItemCommentsPanel'

export function RequestItemPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const role = user?.role
  const queryClient = useQueryClient()
  const canPatchPrices =
    role === 'MANAGER' || role === 'ADMIN' || role === 'ROP' || role === 'ASSISTANT'
  const focusQuoteId = searchParams.get('quoteId')
  const focusCommentId = searchParams.get('commentId')

  useEffect(() => {
    const focusId = focusCommentId ?? focusQuoteId
    if (!focusId) return
    const timer = window.setTimeout(() => {
      const el = document.querySelector(`[data-focus-id="${focusId}"]`)
      if (!el) return
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      el.classList.add('ring-2', 'ring-brand-400', 'ring-offset-2')
      window.setTimeout(() => {
        el.classList.remove('ring-2', 'ring-brand-400', 'ring-offset-2')
      }, 2500)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [focusQuoteId, focusCommentId])

  const [showSiblingMenu, setShowSiblingMenu] = useState(false)
  const [selectedByMatrix, setSelectedByMatrix] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [commentQuoteId, setCommentQuoteId] = useState<string | null>(null)

  const isProcurementActor = role === 'PROCUREMENT' || role === 'ROS'

  const {
    data: item,
    isLoading,
    error: itemError,
  } = useQuery({
    queryKey: ['request-items', id],
    queryFn: () => getItem(id!),
    enabled: !!id,
    retry: (_count, err) =>
      !(axios.isAxiosError(err) && [403, 404].includes(err.response?.status ?? 0)),
  })

  const { data: history = [] } = useQuery({
    queryKey: ['item-history', id],
    queryFn: () => getItemHistory(id!),
    enabled: !!id,
  })

  const { data: procStateData } = useQuery({
    queryKey: ['item-proc-state', id],
    queryFn: () => getProcurementState(id!),
    enabled: !!id && isProcurementActor,
  })
  const myProcState = (procStateData?.state ?? 'NEW') as RequestItemProcurementState
  // РОЗ видит в шапке статус команды (как в карточке заявки), кнопки — по своему.
  const badgeProcState = (procStateData?.teamState ?? myProcState) as RequestItemProcurementState

  const { data: results = [] } = useQuery({
    queryKey: ['procurement-results', id],
    queryFn: () => getProcurementResults(id!),
    enabled: !!id,
  })

  // Курсы валют. Пока `rates === null` (грузятся или не загрузились), цены
  // матриц не считаем и НЕ пишем в позицию — см. useMatrixRates.
  const { rates, pricing } = useMatrixRates()

  // Восстановление/авто-выбор чекбоксов по матрицам:
  //  - если в позиции уже стоит цена матрицы — ищем совпадающую квоту (как было);
  //  - если цена матрицы пуста, но квота этой матрицы существует — авто-выбираем
  //    самую раннюю и записываем её цену в позицию (для манагера/ADMIN/ROP).
  // Источник `results` отсортирован сервером по createdAt desc — переворачиваем,
  // чтобы "первая" квота для каждой матрицы была самой ранней.
  useEffect(() => {
    // Без курсов не считаем ничего: ни авто-выбор чекбоксов (он сравнивает
    // цену позиции с конвертированной ценой квоты), ни тем более PATCH цены.
    if (!item || results.length === 0 || !rates) return
    const sorted = [...results].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    const restored: Record<string, string> = {}
    const autoPatch: Record<string, number> = {}
    for (const r of sorted) {
      const field = PRICE_FIELD_BY_MATRIX[r.sourceType]
      if (!field) continue
      if (restored[r.sourceType]) continue
      // Квоты-«заготовки» (без цены) и квоты с ценой 0 («позиция отсутствует»)
      // в автовыбор для КП не идут — совпадает с бэком (auto-matrix-selection).
      if (r.price == null || Number(r.price) <= 0) continue
      const current = (item as unknown as Record<string, unknown>)[field]
      // Валюта по умолчанию для квот этой страницы — USD (историческое
      // поведение), поэтому подставляем её явно, а не полагаемся на утилиту.
      const converted = convertQuoteToMatrixField(
        Number(r.price),
        r.currency ?? 'USD',
        r.sourceType,
        rates,
      )
      if (current == null) {
        restored[r.sourceType] = r.id
        autoPatch[field] = converted
      } else if (Math.abs(Number(current) - converted) < 0.02) {
        restored[r.sourceType] = r.id
      }
    }
    setSelectedByMatrix((prev) => {
      const merged = { ...restored, ...prev }
      return JSON.stringify(merged) === JSON.stringify(prev) ? prev : merged
    })
    if (canPatchPrices && Object.keys(autoPatch).length > 0) {
      void patchItem(item.id, autoPatch as Parameters<typeof patchItem>[1])
        .then((updated) => {
          queryClient.setQueryData(['request-items', id], updated)
          void queryClient.invalidateQueries({ queryKey: ['requests'] })
        })
        .catch(() => {
          /* RBAC/сеть — авто-выбор просто не сохранится, ругаться не надо */
        })
    }
  }, [item, results, rates, canPatchPrices, id, queryClient])

  if (isLoading) return <PageLoader />
  if (!item) {
    const status = axios.isAxiosError(itemError) ? itemError.response?.status : undefined
    const message =
      status === 403
        ? 'Нет доступа к этой позиции'
        : status === 404
          ? 'Позиция не найдена'
          : 'Не удалось загрузить позицию'
    return <p className="text-sm text-gray-500">{message}</p>
  }

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-500 flex items-center gap-1.5">
        {/* «Очередь» — тем, кому она открывается (у ADMIN закрыта с 18.09,
            крошка убрана 25.09), кроме продажников (белый список). */}
        {canOpenQueue(role) && role !== 'MANAGER' && role !== 'ROP' && (
          <>
            <Link to="/procurement/queue" className="hover:text-gray-900">
              Очередь
            </Link>
            <span>›</span>
          </>
        )}
        <Link to={`/requests/${item.requestId}`} className="hover:text-gray-900">
          Заявка
        </Link>
        <span>›</span>
        <span className="font-mono text-gray-900 font-medium">{item.partNumber}</span>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        <div className="flex-1 min-w-0 space-y-4 w-full">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
              <div className="inline-flex items-center gap-2 min-w-0">
                <h1 className="text-lg sm:text-xl font-mono font-bold text-gray-900 select-text break-words">
                  {item.partNumber}
                </h1>
                <SiblingDropdown
                  itemId={id!}
                  requestId={item.requestId}
                  role={role}
                  userId={user?.id}
                  show={showSiblingMenu}
                  onToggle={() => setShowSiblingMenu((v) => !v)}
                  onClose={() => setShowSiblingMenu(false)}
                />
              </div>
              {item.brand && <div className="text-sm text-gray-500 mt-0.5">{item.brand}</div>}
              <div className="flex gap-2 shrink-0 flex-wrap items-start">
                <PriorityBadge priority={item.priority} />
                {isProcurementActor ? (
                  <ProcItemStatusBadge state={badgeProcState} team={role === 'ROS'} />
                ) : (
                  <ItemStatusBadge status={item.status} />
                )}
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs text-gray-400">Количество</div>
                <div className="font-medium">
                  {item.quantity} {item.unit ?? 'pcs'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Назначен</div>
                <div className="font-medium text-xs truncate">{item.assignedTo?.email ?? '—'}</div>
              </div>
              {item.targetPrice != null && (
                <div>
                  <div className="text-xs text-gray-400">Целевая цена</div>
                  <div className="font-medium">{item.targetPrice}</div>
                </div>
              )}
              {item.noteForProcurement && (
                <div className="col-span-2 sm:col-span-4">
                  <div className="text-xs text-gray-400 mb-0.5">Заметка от менеджера</div>
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5">
                    {item.noteForProcurement}
                  </div>
                </div>
              )}

              <ProcStateButtons role={role} myProcState={myProcState} itemId={id!} />
            </div>
          </div>

          {!pricing.ready && (
            <PricingNotReady isLoading={pricing.isLoading} onRetry={pricing.retry} what="rates" />
          )}

          <QuotesTable
            results={results}
            itemId={id!}
            itemMatrix={item.matrix}
            role={role}
            userId={user?.id}
            rates={rates}
            selectedByMatrix={selectedByMatrix}
            setSelectedByMatrix={setSelectedByMatrix}
            isSaving={isSaving}
            setIsSaving={setIsSaving}
            focusQuoteId={focusQuoteId}
            commentQuoteId={commentQuoteId}
            onSelectCommentQuote={setCommentQuoteId}
          />

          <ItemHistoryPanel history={history} />
        </div>

        <ItemCommentsPanel
          itemId={id!}
          requestId={item.requestId}
          quoteId={commentQuoteId}
          onClearQuote={() => setCommentQuoteId(null)}
          focusCommentId={focusCommentId}
          quotes={results}
        />
      </div>
    </div>
  )
}
